// « Questionnaire santé » (#/sante, 14/10). Three states of the coach's review of the submitted questionnaire (attente / valide /
// échange), then the READ-ONLY answers (?vue=reponses), the immutable snapshot of what was sent. No diagnosis, no risk score, no
// « refusé », no deadline. Shown only under an explicit configuration (health-state.ts): without it, the route returns to the
// home. Also exports HealthNotice, the same state + link used by the training and booking guards.
import { useEffect, useSyncExternalStore } from "react";
import { Check, ChevronLeft, ChevronRight, Clock, MessageCircle } from "lucide-react";
import { Button } from "@ubm/ui/components/button";
import { StatusBar, type Route } from "./parts";
import { ShellActions } from "./shell";
import { ANSWER_LABEL, STATUS_LABEL, health, isConfigured, stateFromHash, useHealth, visibleAnswers, type HealthStatus } from "./health-state";
import { COACHES, messagerie } from "./messagerie-state";
import { useAccount } from "./compte-state";
import { capitalize, longDay } from "./demo-context";

const COPY: Record<HealthStatus, { title: string; text: string }> = {
  attente: { title: "Ton questionnaire a bien été envoyé", text: "Ton coach doit le relire avant que tu puisses commencer tes séances." },
  valide: { title: "Ton coach a relu ton questionnaire", text: "Tu peux maintenant commencer tes séances." },
  echange: { title: "Ton coach souhaite échanger avec toi", text: "Un échange est nécessaire avant de reprendre tes séances." },
};
const ICON: Record<HealthStatus, typeof Clock> = { attente: Clock, valide: Check, echange: MessageCircle };
const at = (iso: string) => { const [day, time] = iso.split("T"); return `${longDay(day!)}${time ? ` à ${time.replace(":", " h ")}` : ""}`; };

const subscribeHash = (l: () => void) => { window.addEventListener("hashchange", l); return () => window.removeEventListener("hashchange", l); };
const readHash = () => window.location.hash;
const withoutVue = (hash: string) => hash.replace(/([?&])vue=reponses&?/, "$1").replace(/[?&]$/, "");
/** The thread of the reviewing coach when known, else the first coach (demo); context = the exact questionnaire route. */
const contact = (label: string, coachName?: string) => {
  const c = COACHES.find(x => x.name === coachName) ?? COACHES[0];
  if (!c) return;
  messagerie.startWithContext(c.id, { label, href: window.location.hash }); window.location.hash = `#/messages?fil=${c.id}`;
};

/** Guard notice (Séance, Réserver): the same status, its sentence and a link to the health page. Never a silent disabled button. */
export function HealthNotice({ status }: { status: HealthStatus }) {
  const Icon = ICON[status];
  return <div className="cc-health-notice" role="status">
    <span className="cc-health-pill" data-status={status}><Icon size={14} strokeWidth={2.4} aria-hidden="true" />{STATUS_LABEL[status]}</span>
    <p>{COPY[status].text}</p>
    <a className="cc-health-link" href="#/sante">Voir mon questionnaire santé</a>
  </div>;
}

export function QuestionnaireSante(_: { go: (route: Route) => void; onOutOfScope: (label: string) => void }) {
  const h = useHealth();
  // Health-data consent of the account (compte-state, Paramètres): without it, no answer value is read nor rendered.
  const consent = useAccount().consents.sensibles;
  const hash = useSyncExternalStore(subscribeHash, readHash, readHash);
  // Same route, new ?etat= (the app re-renders on a route change only): apply the explicit configuration of the URL.
  useEffect(() => { health.syncFromHash(hash); }, [hash]);
  // Not configured: an explicit ?etat= is being applied by the effect (render nothing yet); otherwise back to the home.
  if (!isConfigured(h) && stateFromHash(hash)) return <div className="cc-screen cc-health" />;
  if (!isConfigured(h)) { window.setTimeout(() => { window.location.hash = "#/accueil"; }, 0); return <div className="cc-screen cc-health" />; }
  const snap = h.snapshot!, status = h.review.status, Icon = ICON[status];
  const answers = /vue=reponses/.test(hash);

  if (answers) return <div className="cc-screen cc-account cc-health">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label="Retour au questionnaire santé" onClick={() => { window.location.hash = withoutVue(hash); }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <h1 className="cc-title">Mes réponses</h1>
        <ShellActions />
      </header>
      {/* Current review status (generic, never an answer value): always shown, consent or not. */}
      <span className="cc-health-pill" data-status={status}><Icon size={14} strokeWidth={2.4} aria-hidden="true" />{STATUS_LABEL[status]}</span>
      <p className="cc-health-meta">Envoyé le {at(snap.submittedAt)}</p>
      {/* Review metadata moved here from the « Échange » body (21/10): the detail carries the dates, not the state screen. */}
      {h.review.reviewedAt && <p className="cc-health-meta">Relu le {at(h.review.reviewedAt)}{h.review.coach ? ` · ${h.review.coach}` : ""}</p>}
      {(() => { const v = visibleAnswers(snap, consent);
        return v.kind === "answers" ? <>
          <ol className="cc-health-answers">{v.rows.map(r => <li key={r.id}>
            <p className="cc-health-q">{r.q}</p>
            <p className="cc-health-a">{ANSWER_LABEL[r.a]}</p>
            {r.detail && <p className="cc-health-detail">{r.detail}</p>}
          </li>)}</ol>
          <p className="cc-health-privacy">Ces réponses sont accessibles à toi et aux coachs autorisés.</p></>
        : v.kind === "no-consent" ? <p className="cc-health-meta">Tes réponses de santé ne sont pas affichées : l’accord pour les données de santé n’est pas actif.</p>
        : v.kind === "purged" ? <p className="cc-health-meta">Tes réponses de santé ont été retirées après le retrait de ton accord.</p>
        : <p className="cc-health-meta">Tu préfères en parler directement avec ton coach.</p>; })()}
      <div className="cc-health-actions">
        <Button variant="secondary" size="lg" onClick={() => contact("Changement depuis mon questionnaire santé", h.review.coach)}>Signaler un changement</Button>
      </div>
      {snap.source === "demo" && <p className="cc-home-demo">Démo : réponses fictives, rien n'est envoyé.</p>}
    </div>
  </div>;

  const openAnswers = () => { window.location.hash = `${hash.includes("?") ? hash : `${hash}?`}${hash.includes("?") ? "&" : ""}vue=reponses`; };

  // « Attente » (user override 14/10): one body title (the status, small inline Clock), the exact sentence, the send date as a
  // plain muted line (coach only if known), then « Voir mes réponses » as a row link and « Contacter mon coach » as a text link.
  // The other states below are unchanged.
  if (status === "attente") return <div className="cc-screen cc-account cc-health">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label="Retour à l'accueil" onClick={() => { window.location.hash = "#/accueil"; }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <h1 className="cc-title">Questionnaire santé</h1>
        <ShellActions />
      </header>
      <section className="cc-health-wait" aria-labelledby="sante-titre">
        <h2 id="sante-titre" className="cc-health-wait-title"><Clock size={18} strokeWidth={2.2} aria-hidden="true" />{STATUS_LABEL.attente}</h2>
        <p className="cc-health-text">{COPY.attente.text}</p>
        <p className="cc-health-wait-meta">Envoyé le {at(snap.submittedAt)}{h.review.coach ? ` · ${h.review.coach}` : ""}</p>
      </section>
      <button type="button" className="cc-health-row" onClick={openAnswers}><span>Voir mes réponses</span><ChevronRight size={16} color="#c4c4c9" aria-hidden="true" /></button>
      <Button className="cc-health-textlink" variant="ghost" size="md" onClick={() => contact("À propos de mon questionnaire santé", h.review.coach)}>Contacter mon coach</Button>
      {snap.source === "demo" && <p className="cc-home-demo">Démo : questionnaire et relecture fictifs, rien n'est envoyé.</p>}
    </div>
  </div>;

  // « Échange » (validated simplification, Vincent 21/10): the coach's message, ONE action « Contacter <coach> » and the
  // questionnaire as a discreet link. Status, dates, role and the repeated explanation live in the questionnaire detail
  // (?vue=reponses). The message is the coach's own comment only (never invented for an unknown coach); its trailing demo
  // marker stays in the fixture, it is not shown in the body. Same shell, header, contact() and openAnswers.
  if (status === "echange") {
    const known = COACHES.find(c => c.name === h.review.coach);
    const name = known ? known.name.split(" ")[0] : h.review.coach;
    const message = h.review.comment?.replace(/\s*\(message de démonstration\)$/, "");
    return <div className="cc-screen cc-account cc-health">
      <StatusBar />
      <div className="cc-scroll">
        <header className="cc-account-head">
          <button type="button" className="cc-icon-btn" aria-label="Retour à l'accueil" onClick={() => { window.location.hash = "#/accueil"; }}><ChevronLeft size={18} strokeWidth={2} /></button>
          <h1 className="cc-title">Questionnaire santé</h1>
          <ShellActions />
        </header>
        <section className="cc-health-x" aria-labelledby="sante-titre">
          <h2 id="sante-titre" className="cc-health-x-title">{name ? `${name} souhaite échanger avec toi` : COPY.echange.title}</h2>
          {message && <p className="cc-health-x-message">{message}</p>}
          <Button variant="primary" size="lg" onClick={() => contact("À propos de mon questionnaire santé", h.review.coach)}>{name ? `Contacter ${name}` : "Contacter mon coach"}</Button>
          <button type="button" className="cc-health-x-link" onClick={openAnswers}>Voir mon questionnaire</button>
        </section>
      </div>
    </div>;
  }

  return <div className="cc-screen cc-account cc-health">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label="Retour à l'accueil" onClick={() => { window.location.hash = "#/accueil"; }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <h1 className="cc-title">Questionnaire santé</h1>
        <ShellActions />
      </header>
      {/* « Validé » / « Échange » (user override 14/10): the approved light layout of « Attente ». One heading (status + small inline
          icon), the exact sentence, compact muted dates / coach, the coach's comment as is, then one row link and one text link. */}
      <section className="cc-health-wait" aria-labelledby="sante-titre">
        <h2 id="sante-titre" className="cc-health-wait-title"><Icon size={18} strokeWidth={2.2} aria-hidden="true" />{STATUS_LABEL[status]}</h2>
        <p className="cc-health-text">{COPY[status].text}</p>
        <p className="cc-health-wait-meta">Envoyé le {at(snap.submittedAt)}</p>
        {(h.review.reviewedAt || h.review.coach) && <p className="cc-health-wait-meta">{[h.review.reviewedAt ? `Relu le ${at(h.review.reviewedAt)}` : null, h.review.coach].filter(Boolean).join(" · ")}</p>}
        {h.review.comment && <p className="cc-health-comment">« {h.review.comment} »</p>}
      </section>
      {status === "valide"
        ? <button type="button" className="cc-health-row" onClick={() => { window.location.hash = "#/entrainement"; }}><span>Voir mes séances</span><ChevronRight size={16} color="#c4c4c9" aria-hidden="true" /></button>
        : <button type="button" className="cc-health-row" onClick={() => contact("À propos de mon questionnaire santé", h.review.coach)}><span>Contacter mon coach</span><ChevronRight size={16} color="#c4c4c9" aria-hidden="true" /></button>}
      <Button className="cc-health-textlink" variant="ghost" size="md" onClick={openAnswers}>Voir mes réponses</Button>
      {snap.source === "demo" && <p className="cc-home-demo">Démo : questionnaire et relecture fictifs, rien n'est envoyé.</p>}
    </div>
  </div>;
}
